/**
 * MessageComposeSheet - Sheet per composar missatges ACCESS i CUSTOM
 *
 * ACCESS: mostra preview plantilla + input codi porta (4-6 digits)
 * CUSTOM: textarea lliure amb comptador de caràcters
 */

import { useState, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Send, Loader2, AlertCircle } from 'lucide-react';
import { useCreateMessagingTask, useComposeMessage } from '@/hooks/useMessagingTask';
import { useMessageTemplates } from '@/hooks/useMessagingTask';
import { api } from '@/services/api';
import type { MessageKind, Reservation } from '@guilleumes/types';
import { cn } from '@/lib/utils';
import {
  fillAccessCodeLine,
  interpolateTemplate,
  messageKindLabels,
  messageKindEmojis,
  prepareAccessWithCode,
} from '@/lib/messaging';
import { openWhatsapp } from '@/lib/whatsapp-link';

interface MessageComposeSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reservationId: string;
  messageKind: MessageKind;
  onDone?: () => void;
  /**
   * ACCESS: el text que ha renderitzat el backend sense el codi (no l'ha pogut calcular). Si hi
   * és, la vista prèvia és aquest text amb el codi escrit a la línia «Codi:», en lloc de
   * la plantilla interpolada aquí (que no sap `{remitent}`, `{habNum}`, `{planta}`…).
   */
  baseText?: string;
  /**
   * ACCESS: la tasca que ja ha creat el botó Codi (el backend no ha pogut calcular el codi). Si
   * hi és, el codi escrit s'hi afegeix (`/compose`) en lloc de crear-ne una altra. Sense, se'n crea.
   */
  taskId?: string;
  /** Classes extra del `SheetContent` (p. ex. un `z-index` per sobre del popover a pantalla completa). */
  contentClassName?: string;
  /** WhatsApp obert amb la tasca creada: qui obre el compositor hi penja el «enviat» en tornar (D5). */
  onWhatsappOpened?: (taskId: string, text: string) => void;
}

// Schema de validació per ACCESS
const accessSchema = z.object({
  codi: z.string().regex(/^\d{4,6}$/, 'El codi ha de tenir entre 4 i 6 digits'),
});

// Schema per CUSTOM
const customSchema = z.object({
  text: z.string().min(1, 'El missatge no pot estar buit').max(1000, 'Màxim 1000 caràcters'),
});

type AccessFormData = z.infer<typeof accessSchema>;
type CustomFormData = z.infer<typeof customSchema>;

// Emojis de lib/messaging
const kindEmoji = messageKindEmojis;

export function MessageComposeSheet({
  open,
  onOpenChange,
  reservationId,
  messageKind,
  onDone,
  baseText,
  taskId,
  contentClassName,
  onWhatsappOpened,
}: MessageComposeSheetProps) {
  const [reservation, setReservation] = useState<Reservation | null>(null);
  const [isLoadingReservation, setIsLoadingReservation] = useState(false);
  const [whatsappLink, setWhatsappLink] = useState<string>('');
  const [isCreating, setIsCreating] = useState(false);

  const { data: templates, isLoading: isLoadingTemplates } = useMessageTemplates(messageKind);
  const createTask = useCreateMessagingTask();
  const composeMessage = useComposeMessage();

  // Form ACCESS
  const accessForm = useForm<AccessFormData>({
    resolver: zodResolver(accessSchema),
    defaultValues: { codi: '' },
  });

  // Form CUSTOM
  const customForm = useForm<CustomFormData>({
    resolver: zodResolver(customSchema),
    defaultValues: { text: '' },
  });

  // Carregar reserva quan s'obre
  useEffect(() => {
    if (open && reservationId) {
      setIsLoadingReservation(true);
      api
        .get(`/reservations/${reservationId}`)
        .then((res) => setReservation(res.data))
        .catch(() => toast.error('Error carregant reserva'))
        .finally(() => setIsLoadingReservation(false));
    }
  }, [open, reservationId]);

  // Reset forms quan es tanca
  useEffect(() => {
    if (!open) {
      accessForm.reset();
      customForm.reset();
      setWhatsappLink('');
    }
  }, [open, accessForm, customForm]);

  // Trobar plantilla correcta segons idioma de la reserva
  const activeTemplate = useMemo(() => {
    if (!templates || templates.length === 0) return null;

    // Determinar idioma de la reserva (fallback a 'CA')
    const lang =
      reservation?.travellers?.[0]?.nacionalitat === 'ESP'
        ? 'ES'
        : reservation?.travellers?.[0]?.nacionalitat === 'FRA'
          ? 'FR'
          : reservation?.travellers?.[0]?.nacionalitat === 'GBR'
            ? 'EN'
            : 'CA';

    // Buscar plantilla per kind + idioma
    const template = templates.find((t) => t.language === lang && t.active);
    return template || templates.find((t) => t.active) || templates[0];
  }, [templates, reservation]);

  // Preview renderitzada per ACCESS
  const accessPreview = useMemo(() => {
    const codi = accessForm.watch('codi') || 'XXXX';
    // El text del backend (amb remitent, habitació, planta i wifi de debò): només hi falta el codi.
    if (baseText) return fillAccessCodeLine(baseText, codi);

    if (!activeTemplate?.content) return '';

    const vars: Record<string, string> = {
      nom: reservation?.guestName || reservation?.clientName || '{nom}',
      hab: reservation?.accommodation?.nom || '{hab}',
      codi,
      wifi: 'WiFi_Guilleumes', // Això vindria del config, però fem mock per preview
      telefon: reservation?.travellers?.[0]?.telefon || '{telefon}',
      staff: 'Staff', // S'omplirà al backend
    };

    return interpolateTemplate(activeTemplate.content, vars);
  }, [activeTemplate, accessForm.watch('codi'), reservation, baseText]);

  // Preview per CUSTOM (el text escrit)
  const customPreview = customForm.watch('text');

  // Obrir WhatsApp per ACCESS
  const handleAccessSubmit = async (data: AccessFormData) => {
    setIsCreating(true);
    try {
      // Amb la tasca del botó Codi, s'hi afegeix el codi; sense, se'n crea una (§4.7).
      const result = await prepareAccessWithCode(
        { reservationId, codi: data.codi, existingTaskId: taskId },
        { createTask: createTask.mutateAsync, composeTask: composeMessage.mutateAsync },
      );

      setWhatsappLink(result.whatsappLink);

      if (!result.phoneValid || !result.whatsappLink) {
        toast.warning('Telèfon no vàlid — tasca creada. Copia el missatge manualment.', {
          duration: 8000,
        });
        // `/compose` amb un telèfon no vàlid torna el text buit: el de la vista prèvia.
        const copyText = result.text || (baseText ? fillAccessCodeLine(baseText, data.codi) : '');
        navigator.clipboard?.writeText(copyText).catch(() => {});
      } else {
        openWhatsapp(result.whatsappLink);
        onWhatsappOpened?.(result.taskId, result.text);
        toast.success('Missatge preparat i WhatsApp obert');
      }
      onOpenChange(false);
      onDone?.();
    } catch (error) {
      toast.error('Error creant la tasca de missatgeria');
      console.error(error);
    } finally {
      setIsCreating(false);
    }
  };

  // Obrir WhatsApp per CUSTOM
  const handleCustomSubmit = async (data: CustomFormData) => {
    setIsCreating(true);
    try {
      const result = await createTask.mutateAsync({
        reservationId,
        messageKind: 'CUSTOM',
        customText: data.text,
      });

      setWhatsappLink(result.whatsappLink);

      if (!result.phoneValid || !result.whatsappLink) {
        toast.warning('Telèfon no vàlid — tasca creada. Copia el missatge manualment.', {
          duration: 8000,
        });
        navigator.clipboard?.writeText(result.text).catch(() => {});
      } else {
        openWhatsapp(result.whatsappLink);
        onWhatsappOpened?.(result.taskId, result.text);
        toast.success('Missatge enviat i WhatsApp obert');
      }
      onOpenChange(false);
      onDone?.();
    } catch (error) {
      toast.error('Error creant la tasca de missatgeria');
      console.error(error);
    } finally {
      setIsCreating(false);
    }
  };

  const isLoading = isLoadingReservation || isLoadingTemplates;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className={cn('w-full sm:max-w-lg overflow-y-auto', contentClassName)}>
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center gap-2">
            <span>{kindEmoji[messageKind]}</span>
            {messageKindLabels[messageKind]}
          </SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Info de la reserva */}
            {reservation && (
              <div className="bg-muted rounded-md p-3 text-sm">
                <p className="font-medium">{reservation.guestName || reservation.clientName}</p>
                <p className="text-muted-foreground">
                  {reservation.accommodation?.nom} ·{' '}
                  {reservation.checkIn
                    ? (() => {
                        const s = String(reservation.checkIn);
                        return new Date(s.includes('T') ? s : s + 'T12:00:00').toLocaleDateString(
                          'ca-ES',
                        );
                      })()
                    : '—'}
                </p>
              </div>
            )}

            {messageKind === 'ACCESS' ? (
              <form onSubmit={accessForm.handleSubmit(handleAccessSubmit)} className="space-y-6">
                {/* Input codi */}
                <div className="space-y-2">
                  <Label htmlFor="codi" className="flex items-center gap-2">
                    Codi porta
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="codi"
                    type="text"
                    inputMode="numeric"
                    placeholder="Ex: 9288"
                    maxLength={6}
                    {...accessForm.register('codi')}
                    className={cn(
                      'font-mono text-lg tracking-wider',
                      accessForm.formState.errors.codi && 'border-red-500',
                    )}
                  />
                  {accessForm.formState.errors.codi && (
                    <p className="text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {accessForm.formState.errors.codi.message}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Introdueix el codi numèric de la porta (4-6 digits)
                  </p>
                </div>

                {/* Preview */}
                <div className="space-y-2">
                  <Label>Preview del missatge</Label>
                  <div className="bg-green-50 border border-green-200 rounded-md p-4 font-mono text-sm whitespace-pre-wrap min-h-[120px]">
                    {accessPreview || (
                      <span className="text-muted-foreground italic">
                        Escriu el codi per veure la preview...
                      </span>
                    )}
                  </div>
                </div>

                <SheetFooter className="pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isCreating}
                  >
                    Cancel·lar
                  </Button>
                  <Button
                    type="submit"
                    disabled={isCreating || !accessForm.formState.isValid}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {isCreating ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Obrir WhatsApp
                  </Button>
                </SheetFooter>
              </form>
            ) : (
              <form onSubmit={customForm.handleSubmit(handleCustomSubmit)} className="space-y-6">
                {/* Textarea */}
                <div className="space-y-2">
                  <Label htmlFor="text" className="flex items-center gap-2">
                    Missatge personalitzat
                    <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="text"
                    placeholder="Escriu el teu missatge..."
                    rows={6}
                    maxLength={1000}
                    {...customForm.register('text')}
                    className={cn(
                      'font-mono text-sm resize-none',
                      customForm.formState.errors.text && 'border-red-500',
                    )}
                  />
                  <div className="flex justify-between text-xs">
                    {customForm.formState.errors.text ? (
                      <p className="text-red-500">{customForm.formState.errors.text.message}</p>
                    ) : (
                      <span />
                    )}
                    <span
                      className={cn(
                        'text-muted-foreground',
                        (customForm.watch('text')?.length || 0) > 900 && 'text-yellow-600',
                        (customForm.watch('text')?.length || 0) >= 1000 && 'text-red-500',
                      )}
                    >
                      {customForm.watch('text')?.length || 0} / 1000
                    </span>
                  </div>
                </div>

                {/* Preview */}
                {customPreview && (
                  <div className="space-y-2">
                    <Label>Preview</Label>
                    <div className="bg-green-50 border border-green-200 rounded-md p-4 font-mono text-sm whitespace-pre-wrap">
                      {customPreview}
                    </div>
                  </div>
                )}

                <SheetFooter className="pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={isCreating}
                  >
                    Cancel·lar
                  </Button>
                  <Button
                    type="submit"
                    disabled={isCreating || !customForm.formState.isValid}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {isCreating ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Obrir WhatsApp
                  </Button>
                </SheetFooter>
              </form>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
